<script lang="ts">
  import { applyAction } from '$app/forms';
  import Button from '$components/elements/Button.svelte';
  import Checkbox from '$components/elements/Checkbox.svelte';
  import Form from '$components/elements/Form.svelte';
  import Input from '$components/elements/Input.svelte';
  import AmountButton from '$components/elements/registration/AmountButton.svelte';
  import Flex from '$components/layout/Flex.svelte';
  import Grid from '$components/layout/Grid.svelte';
  import MediaQuery from '$components/utils/MediaQuery.svelte';
  import Text from '$components/utils/Text.svelte';
  import { globalError, showEmailBlocker } from '$lib/stores';
  import { globalValues } from '$lib/stores/global-values';
  import { validate, type Registration as Values } from '$src/routes/backend/registration/form';
  import type { SubmitFunction } from '@sveltejs/kit';
  import { _ } from 'svelte-i18n';

  export let locale: 'cs' | 'en' = 'cs';
  let loading = false;

  const submitRegister: SubmitFunction = ({ data, cancel }) => {
    loading = true;
    const values = {
      ...Object.fromEntries(data),
      amount: data.get('amount')?.toString() ?? '0',
      amountCustom: data.get('amountCustom')?.toString()
    } as Values;
    const validateResponse = validate(values);
    if (validateResponse !== null) {
      globalError.set(validateResponse.message);
      loading = false;
      cancel();
    }

    return async ({ result, update }) => {
      await applyAction(result);
      if ('data' in result && typeof result.data === 'object' && 'message' in result.data) {
        globalError.set(result.data.message as string);
      }
      await update({ reset: false });
      showEmailBlocker.set(
        result.type === 'success' && { type: 'verification', email: data.get('email') as string }
      );
      loading = false;
    };
  };

  let customAmount: string | undefined = undefined;
  let selectedAmount: string | undefined = undefined;

  $: selectedAmount = customAmount;

  // This function updates both customAmount and selectedAmount
  function selectAmount(amount: string) {
    customAmount = amount; // This will also update selectedAmount due to the reactive statement
  }
</script>

<div class="registration">
  <Form submit={submitRegister} action="/backend/registration">
    <MediaQuery query="screen and (min-width: 720px)" let:matches>
      {#if matches}
        <Button
          iconOnLeft
          clazz="button--caramel-border"
          iconSize={15}
          iconViewBox={15}
          icon="arrow-left"
          iconColor="#C99E77"
          href={$_('page.club.registration.back-link', { locale })}
          ><Text key="page.club.registration.back-button" {locale} /></Button
        >
      {/if}
      <Flex margin="40px 0 40px 0" direction="column" gap="40px" alignItems="stretch" width="100%">
        <Flex gap="23px" direction="column" alignItems="flex-start" width="100%">
          <h1><Text key="page.club.registration.registration.heading" {locale} /></h1>
          <p class="registration__text">
            <Text key="page.club.registration.registration.text" {locale} />
          </p>
        </Flex>
        <Input
          type="email"
          name="email"
          label={$_('page.club.registration.registration.email', { locale })}
          required
        />
        <Input
          type="text"
          name="fname"
          label={$_('page.club.registration.registration.fname', { locale })}
          required
        />
        <Input
          type="text"
          name="lname"
          label={$_('page.club.registration.registration.lname', { locale })}
          required
        />
        <Input
          type="tel"
          name="phone"
          label={$_('page.club.registration.registration.phone', { locale })}
          required
        />
        <Input
          type="text"
          name="address"
          label={$_('page.club.registration.support-us.address', { locale })}
          required
        />
        <Input
          type="password"
          name="password"
          label={$_('page.club.registration.registration.password', { locale })}
          required
        />
        <Input
          type="password"
          name="passwordConfirm"
          label={$_('page.club.registration.registration.password-confirmation', { locale })}
          required
        />
      </Flex>
      <div class="registration__divider" />
      <Flex margin="40px 0 40px 0" direction="column" gap="40px" alignItems="stretch" width="100%">
        <Flex gap="23px" direction="column" alignItems="flex-start" width="100%">
          <h1><Text key="page.club.registration.support-us.heading" {locale} /></h1>
          <p class="registration__text">
            <Text key="page.club.registration.support-us.text" {locale} />
          </p>
        </Flex>
        {#if !matches}
          <div class="registration__divider-small" />
        {/if}
        <Grid templateColumns="1fr 1fr 1fr" gap="32px">
          <AmountButton
            name="amount"
            value={$globalValues.registerMonetaryAmount1}
            active={customAmount === undefined || customAmount === ''}
            required={customAmount === undefined || customAmount === ''}
            on:click={() => selectAmount($globalValues.registerMonetaryAmount1.toString())}
          />
          <AmountButton
            name="amount"
            value={$globalValues.registerMonetaryAmount2}
            active={customAmount === undefined || customAmount === ''}
            required={customAmount === undefined || customAmount === ''}
            on:click={() => selectAmount($globalValues.registerMonetaryAmount2.toString())}
          />
          <AmountButton
            name="amount"
            value={$globalValues.registerMonetaryAmount3}
            active={customAmount === undefined || customAmount === ''}
            required={customAmount === undefined || customAmount === ''}
            on:click={() => selectAmount($globalValues.registerMonetaryAmount3.toString())}
          />
        </Grid>
        <input type="hidden" name="selectedAmount" bind:value={selectedAmount} />
        <Input
          type="number"
          name="amountCustom"
          label={$_('page.club.registration.support-us.amount', { locale })}
          min={1}
          bind:value={customAmount}
        />
        <Checkbox name="terms" id="0" required>
          <Text key="page.club.registration.terms.1" {locale} />
          <a href={$_('page.club.registration.terms.link', { locale })} target="_blank"
            ><Text key="page.club.registration.terms.2" {locale} /></a
          ><Text key="page.club.registration.terms.3" {locale} />
        </Checkbox>
        {#if !matches}
          <div class="registration__divider-small" />
        {/if}
        <Button width="100%" submit {loading}
          ><Text key="page.club.registration.continue-button" {locale} /></Button
        >
        <div class="registration__divider-small" />
        <p class="registration__donate">
          <Text key="page.club.registration.donate.1" {locale} />
          <a href={$_('page.club.link.donate', { locale })}
            ><Text key="page.club.registration.donate.2" {locale} /></a
          >
        </p>
      </Flex>
    </MediaQuery>
  </Form>
</div>

<style lang="scss">
  .registration {
    width: 560px;
    height: auto;
    padding-left: 50px;
    padding-right: 50px;
    padding-top: 48px;
    margin-top: 149px;
    border-radius: 13px;
    border: 1.5px solid rgba($color-medium-gray, 0.8);
    background-color: $color-white;
    align-self: center;

    h1 {
      font-size: 28px;
    }
    &__text {
      font-weight: 400;
      font-size: 17px;
      line-height: 30px;
      color: rgba($color-dark-gray, 0.6);
    }

    a {
      font-weight: 600;
      text-decoration: underline;
      color: $color-dark-gray;
    }
    &__donate {
      font-size: 16px;
      color: rgba($color-dark-gray, 0.6);
      a {
        font-weight: 700;
        color: $color-dark-gray;
        text-decoration: underline;
      }
    }

    &__divider {
      height: 1px;
      background-color: rgba($color-medium-gray, 0.8);
      width: 100%;
      margin-left: -50px;
      width: calc(100% + 100px);
    }
    &__divider-small {
      height: 1px;
      background-color: $color-medium-gray;
      width: 100%;
    }
  }

  @include mobile {
    .registration {
      width: 100vw;
      height: auto;
      padding-left: 26px;
      padding-right: 26px;
      padding-top: 56px;
      margin-top: 20px;
      border-radius: 0;
      border: none;

      h1 {
        font-weight: 800;
        font-size: 28px;
        line-height: 32px;

        width: 100%;
        text-align: center;
        align-self: center;
      }
      &__text {
        font-size: 16px;
      }

      a {
        font-weight: 600;
      }
      &__donate {
        text-align: center;
        a {
          font-weight: 700;
          color: $color-dark-gray;
        }
      }

      &__divider {
        height: 1px;
        background-color: rgba($color-medium-gray, 0.8);
        width: 100%;
        margin-left: -10px;
        width: calc(100% + 20px);
      }

      &__divider-small {
        height: 1px;
        background-color: $color-medium-gray;
        width: 100%;
      }
    }
  }
</style>
